<script lang="ts" generics="TValues, TValue extends unknown">
    import type { Field, Form } from '@jmvdevelop/svelte-form';
    import { cx } from '$lib/utils/cx.js';
    import type { ChangeEvent } from '$lib/utils/types.js';
    import type { HTMLInputAttributes } from 'svelte/elements';
    import type { Merge } from 'type-fest';
    import type { MeltActionReturn } from '@melt-ui/svelte/internal/types';

    export let type: string = 'text';

    export let form: Form<TValues, string>;
    export let field: Field<TValues, TValue>;
    export let name: string | undefined = undefined;

    export let toText: (value: TValue) => string | null | undefined;
    export let fromText: (string: string) => TValue;

    export let onKeyPress: HTMLInputAttributes['on:keypress'] = undefined;

    export let defaultClass: string = 'control-form';
    export let invalidClass: string = 'control-form--invalid';
    export let id: string | null = null;

    export let meltAction: (node: HTMLElement) => MeltActionReturn<'input'> = () => {
        return {};
    };

    let className: string | null = null;
    export { className as class };

    function onInput(event: ChangeEvent<HTMLInputElement>) {
        const v = event.currentTarget.value;
        form.dispatch(form.actions().changeValue(fromText(v), field));
    }

    function onBlur() {
        form.dispatch(form.actions().blur(field));
    }

    function onFocus() {
        form.dispatch(form.actions().focus(field));
    }

    $: v = form.stores().fieldValue(field);
    $: nbSubmits = form.stores().nbSubmits();
    $: isAlreadyBlur = form.stores().isAlreadyBlur(field);
    $: hasAnyErrors = form.stores().hasFieldAnyErrors(field);
    $: fieldId = form.getFieldId(field);
    $: valueText = toText($v) ?? '';

    type $$Props = Merge<
        HTMLInputAttributes,
        {
            type?: string;
            class?: string;
            form: Form<TValues, string>;
            field: Field<TValues, TValue>;
            name?: string | undefined;
            toText: (value: TValue) => string | null | undefined;
            fromText: (string: string) => TValue;
            onKeyPress?: HTMLInputAttributes['on:keypress'];
            defaultClass?: string;
            invalidClass?: string;
            meltAction?: (node: HTMLElement) => MeltActionReturn<'input'>;
        }
    >;
</script>

<input
    use:meltAction
    id={id ?? fieldId}
    {type}
    {name}
    class={cx(
        defaultClass,
        ($nbSubmits > 0 || $isAlreadyBlur) && $hasAnyErrors ? invalidClass : '',
        className ?? '',
    )}
    value={valueText}
    on:focus={onFocus}
    on:blur={onBlur}
    on:input={onInput}
    on:keypress={onKeyPress}
    {...$$restProps}
/>
